import { useState } from 'react'
import { useParams, useNavigate, Navigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../store/AuthContext'
import { useToast } from '../../components/Toast'
import employeesService from '../../services/employeesService'
import scoresService from '../../services/scoresService'
import attitudesService from '../../services/attitudesService'
import { MonthlyScore, AttitudeCategory } from '../../services/types'
import { AttitudeCard } from '../../components/AttitudeCard'
import { ConfirmModal } from '../../components/ConfirmModal'

const MONTH_NAMES = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
]

const MONTH_FULL_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

const QUARTER_MONTHS: Record<number, number[]> = {
  1: [1, 2, 3],
  2: [4, 5, 6],
  3: [7, 8, 9],
  4: [10, 11, 12],
}

const getQuarterLabel = (month: number) => {
  if (month <= 3) return 'Q1'
  if (month <= 6) return 'Q2'
  if (month <= 9) return 'Q3'
  return 'Q4'
}

const ITEMS_PER_PAGE = 6

export const EmployeeDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()

  const employeeId = Number(id)
  const now = new Date()
  const currentYear = now.getFullYear()

  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [selectedQuarter, setSelectedQuarter] = useState<number | null>(null)
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE)
  const [editingScore, setEditingScore] = useState<MonthlyScore | null>(null)
  const [editAttitudes, setEditAttitudes] = useState<Set<number>>(new Set())
  const [editObservation, setEditObservation] = useState('')
  const [activeEditTab, setActiveEditTab] = useState<'positive' | 'negative'>('positive')
  const [showAntifoundationModal, setShowAntifoundationModal] = useState(false)
  const [antifoundationId, setAntifoundationId] = useState<number | null>(null)

  const canEdit = user?.role === 'admin' || user?.role === 'manager'

  // Access control: viewers can only see their own profile
  const { data: allEmployees = [], isLoading: isLoadingEmployees } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeesService.getAll(),
  })

  const myEmployee = allEmployees.find((e) => e.user_detail?.id === user?.id)

  // If viewer, redirect to own profile
  if (!isLoadingEmployees && user?.role === 'viewer') {
    if (!myEmployee) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Seu perfil de funcionário não foi encontrado.</p>
        </div>
      )
    }
    if (employeeId !== myEmployee.id) {
      return <Navigate to={`/funcionarios/${myEmployee.id}`} replace />
    }
  }

  // Fetch employee
  const { data: employee, isLoading: isLoadingEmployee } = useQuery({
    queryKey: ['employee', employeeId],
    queryFn: () => employeesService.getById(employeeId),
    enabled: !!employeeId,
  })

  // Fetch scores for this employee and year
  const { data: scores = [], isLoading: isLoadingScores } = useQuery({
    queryKey: ['scores', employeeId, selectedYear],
    queryFn: () => scoresService.getAll({ employee: employeeId, year: selectedYear }),
    enabled: !!employeeId,
  })

  // Fetch quarterly chart
  const { data: quarterlyChart } = useQuery({
    queryKey: ['quarterly-chart', employeeId, selectedYear],
    queryFn: () => scoresService.getQuarterlyChart(employeeId, selectedYear),
    enabled: !!employeeId,
  })

  // Fetch attitudes (for editing)
  const { data: attitudes = [] } = useQuery({
    queryKey: ['attitudes'],
    queryFn: () => attitudesService.getAll(),
    enabled: canEdit,
  })

  // Fetch accumulated chart data (all time)
  const { data: accumulatedChart, isLoading: isLoadingAccumulated } = useQuery({
    queryKey: ['accumulated-chart', employeeId],
    queryFn: () => scoresService.getAccumulatedChart(employeeId),
    enabled: !!employeeId,
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ scoreId, data }: { scoreId: number; data: { selected_attitudes: number[]; observation?: string } }) =>
      scoresService.update(scoreId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scores', employeeId] })
      queryClient.invalidateQueries({ queryKey: ['quarterly-chart', employeeId] })
      queryClient.invalidateQueries({ queryKey: ['accumulated-chart', employeeId] })
      queryClient.invalidateQueries({ queryKey: ['quarterly-summary'] })
      queryClient.invalidateQueries({ queryKey: ['employee', employeeId] })
      setEditingScore(null)
      toast.success('Avaliação atualizada com sucesso!')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar avaliação.')
    },
  })

  // Monthly points data for chart
  const monthlyPoints: { month: number; points: number }[] = Array.from({ length: 12 }, (_, i) => {
    const month = i + 1
    const score = scores.find((s) => s.month === month && !s.is_reversed)
    return { month, points: score ? score.points_snapshot : 0 }
  })

  // Quarterly averages
  const quarterAverages = [1, 2, 3, 4].map((q) => {
    const qData = quarterlyChart?.quarters?.find((qd) => qd.quarter === q)
    return { quarter: q, average: qData?.average ?? 0 }
  })

  // Active (non-reversed) scores, filtered by quarter, sorted by month desc
  const activeScores = scores
    .filter((s) => !s.is_reversed)
    .filter((s) => selectedQuarter === null || QUARTER_MONTHS[selectedQuarter].includes(s.month))
    .sort((a, b) => b.month - a.month)

  // Paginated scores
  const paginatedScores = activeScores.slice(0, visibleCount)
  const hasMore = visibleCount < activeScores.length

  // Reset pagination when filters change
  const handleYearChange = (year: number) => {
    setSelectedYear(year)
    setVisibleCount(ITEMS_PER_PAGE)
  }

  const handleQuarterChange = (quarter: number | null) => {
    setSelectedQuarter(quarter)
    setVisibleCount(ITEMS_PER_PAGE)
  }

  // Edit handlers
  const openEdit = (score: MonthlyScore) => {
    setEditingScore(score)
    setEditAttitudes(new Set(score.selected_attitudes))
    setEditObservation(score.observation || '')
    setActiveEditTab('positive')
  }

  const toggleEditAttitude = (attitudeId: number) => {
    const attitude = attitudes.find((a) => a.id === attitudeId)
    if (!attitude) return

    const newSelected = new Set(editAttitudes)
    if (newSelected.has(attitudeId)) {
      newSelected.delete(attitudeId)
      setEditAttitudes(newSelected)
      return
    }

    if (attitude.is_special_antifoundation) {
      setAntifoundationId(attitudeId)
      setShowAntifoundationModal(true)
      return
    }

    newSelected.add(attitudeId)
    setEditAttitudes(newSelected)
  }

  const confirmAntifoundation = () => {
    if (antifoundationId) {
      const newSelected = new Set(editAttitudes)
      newSelected.add(antifoundationId)
      setEditAttitudes(newSelected)
    }
    setShowAntifoundationModal(false)
    setAntifoundationId(null)
  }

  const calculateEditPoints = (): number => {
    const selected = Array.from(editAttitudes)
      .map((attId) => attitudes.find((a) => a.id === attId))
      .filter(Boolean) as AttitudeCategory[]

    const positives = selected.filter((a) => a.type === 'positive')
    const highestPositive = positives.length > 0 ? Math.max(...positives.map((a) => a.points)) : 0
    const negatives = selected.filter((a) => a.type === 'negative')
    const totalNegative = negatives.reduce((sum, a) => sum + a.points, 0)
    return highestPositive + totalNegative
  }

  const saveEdit = () => {
    if (!editingScore) return
    if (editAttitudes.size === 0) {
      toast.warning('Selecione ao menos uma atitude.')
      return
    }
    updateMutation.mutate({
      scoreId: editingScore.id,
      data: {
        selected_attitudes: Array.from(editAttitudes),
        observation: editObservation.trim() || undefined,
      },
    })
  }

  // Chart scaling
  const maxChartValue = Math.max(...monthlyPoints.map((m) => Math.abs(m.points)), 1)

  if (isLoadingEmployee || isLoadingEmployees) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Carregando perfil...</p>
      </div>
    )
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">404</div>
        <p className="text-gray-600 dark:text-gray-400">Funcionário não encontrado.</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 text-primary hover:text-primary-hover font-medium"
        >
          Voltar
        </button>
      </div>
    )
  }

  const positiveAttitudes = attitudes.filter((a) => a.type === 'positive').sort((a, b) => a.display_order - b.display_order)
  const negativeAttitudes = attitudes.filter((a) => a.type === 'negative').sort((a, b) => a.display_order - b.display_order)

  return (
    <div>
      {/* Header with name + year selector */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-400"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {employee.full_name}
          </h1>
        </div>

        <select
          value={selectedYear}
          onChange={(e) => handleYearChange(Number(e.target.value))}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent font-medium"
        >
          <option value={currentYear}>{currentYear}</option>
          <option value={currentYear - 1}>{currentYear - 1}</option>
          <option value={currentYear - 2}>{currentYear - 2}</option>
        </select>
      </div>

      {/* Employee Info Card */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 h-16 w-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-semibold">
            {employee.full_name[0]}
          </div>
          <div className="flex-1">
            <p className="text-gray-600 dark:text-gray-400">
              {employee.primary_company_detail?.name}
              {' \u2022 '}
              <span className={employee.is_ownership_eligible ? 'text-success' : 'text-gray-500'}>
                {employee.is_ownership_eligible ? 'Elegível' : 'Não elegível'}
              </span>
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {employee.email}
            </p>
          </div>
          <div className="text-right flex gap-6 items-start">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Acumulado</p>
              <p className={`text-3xl font-bold ${employee.total_points > 0 ? 'text-success' : employee.total_points < 0 ? 'text-danger' : 'text-gray-600 dark:text-gray-400'}`}>
                {employee.total_points > 0 ? '+' : ''}{employee.total_points}
              </p>
            </div>
            {employee.is_ownership_eligible && (() => {
              const totalEligible = allEmployees
                .filter((e) => e.is_ownership_eligible && e.total_points > 0)
                .reduce((sum, e) => sum + e.total_points, 0)
              const ownershipPct = totalEligible > 0 && employee.total_points > 0
                ? (employee.total_points / totalEligible) * 100
                : 0
              return (
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">% Ownership</p>
                  <p className="text-3xl font-bold text-primary">
                    {ownershipPct > 0 ? `${ownershipPct.toFixed(2)}%` : '-'}
                  </p>
                </div>
              )
            })()}
          </div>
        </div>
      </div>

      {/* Link to company dashboard */}
      {employee.primary_company_detail?.id && (
        <div className="mb-6 text-right">
          <button
            onClick={() => navigate(`/empresas/${employee.primary_company_detail.id}/dashboard`)}
            className="text-sm text-primary hover:text-primary-hover transition-colors"
          >
            Ver faturamento e lucratividade da empresa →
          </button>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {quarterAverages.map((q) => (
          <div key={q.quarter} className="bg-white dark:bg-gray-800 rounded-card shadow p-4 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Q{q.quarter}</p>
            <p className={`text-2xl font-bold ${q.average > 0 ? 'text-success' : q.average < 0 ? 'text-danger' : 'text-gray-400'}`}>
              {q.average > 0 ? '+' : ''}{q.average}
            </p>
          </div>
        ))}
      </div>

      {/* Accumulated Line Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          Pontos Reais Acumulados
        </h3>

        {isLoadingAccumulated ? (
          <div className="h-64 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : !accumulatedChart?.data_points?.length ? (
          <div className="h-64 flex items-center justify-center">
            <p className="text-gray-500 dark:text-gray-400">Sem dados acumulados disponíveis.</p>
          </div>
        ) : (() => {
          const points = accumulatedChart.data_points.filter((p) => !p.is_decay)
          const decayPoints = accumulatedChart.data_points.filter((p) => p.is_decay)
          if (points.length === 0) return (
            <div className="h-64 flex items-center justify-center">
              <p className="text-gray-500 dark:text-gray-400">Sem dados acumulados disponíveis.</p>
            </div>
          )

          const allValues = accumulatedChart.data_points.map((p) => p.accumulated_total)
          const maxVal = Math.max(...allValues, 1)
          const minVal = Math.min(...allValues, 0)
          const range = maxVal - minVal || 1

          const padding = { top: 30, right: 40, bottom: 50, left: 50 }
          const chartW = 700
          const chartH = 250
          const innerW = chartW - padding.left - padding.right
          const innerH = chartH - padding.top - padding.bottom

          // All data points in order (including decay)
          const allPoints = accumulatedChart.data_points
          const totalPoints = allPoints.length

          const getX = (i: number) => padding.left + (totalPoints > 1 ? (i / (totalPoints - 1)) * innerW : innerW / 2)
          const getY = (val: number) => padding.top + innerH - ((val - minVal) / range) * innerH

          // Build the line path (connecting all points)
          const linePath = allPoints
            .map((p, i) => `${i === 0 ? 'M' : 'L'} ${getX(i).toFixed(1)} ${getY(p.accumulated_total).toFixed(1)}`)
            .join(' ')

          // Area fill
          const areaPath = `${linePath} L ${getX(totalPoints - 1).toFixed(1)} ${(padding.top + innerH).toFixed(1)} L ${getX(0).toFixed(1)} ${(padding.top + innerH).toFixed(1)} Z`

          // Y-axis grid lines (5 steps)
          const ySteps = 5
          const gridLines = Array.from({ length: ySteps + 1 }, (_, i) => {
            const val = minVal + (range / ySteps) * i
            return { val: Math.round(val), y: getY(val) }
          })

          return (
            <div className="overflow-x-auto">
              <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full min-w-[500px]" preserveAspectRatio="xMidYMid meet">
                {/* Grid lines */}
                {gridLines.map((g, i) => (
                  <g key={i}>
                    <line
                      x1={padding.left}
                      y1={g.y}
                      x2={chartW - padding.right}
                      y2={g.y}
                      stroke="currentColor"
                      className="text-gray-200 dark:text-gray-700"
                      strokeWidth={0.5}
                    />
                    <text
                      x={padding.left - 8}
                      y={g.y + 4}
                      textAnchor="end"
                      className="fill-gray-500 dark:fill-gray-400"
                      fontSize={10}
                    >
                      {g.val}
                    </text>
                  </g>
                ))}

                {/* Zero line */}
                {minVal < 0 && (
                  <line
                    x1={padding.left}
                    y1={getY(0)}
                    x2={chartW - padding.right}
                    y2={getY(0)}
                    stroke="currentColor"
                    className="text-gray-400 dark:text-gray-500"
                    strokeWidth={1}
                    strokeDasharray="4 4"
                  />
                )}

                {/* Area gradient */}
                <defs>
                  <linearGradient id="accAreaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
                  </linearGradient>
                </defs>

                {/* Area fill */}
                <path d={areaPath} fill="url(#accAreaGrad)" />

                {/* Line */}
                <path d={linePath} fill="none" stroke="#3b82f6" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />

                {/* Data points */}
                {allPoints.map((p, i) => (
                  <g key={i}>
                    <circle
                      cx={getX(i)}
                      cy={getY(p.accumulated_total)}
                      r={p.is_decay ? 5 : 4}
                      fill={p.is_decay ? '#ef4444' : '#3b82f6'}
                      stroke="white"
                      strokeWidth={2}
                    />
                    {/* Value label on every point if < 12 points, else every other */}
                    {(totalPoints <= 12 || i % 2 === 0 || p.is_decay) && (
                      <text
                        x={getX(i)}
                        y={getY(p.accumulated_total) - 10}
                        textAnchor="middle"
                        className={p.is_decay ? 'fill-red-500' : 'fill-gray-700 dark:fill-gray-300'}
                        fontSize={9}
                        fontWeight={p.is_decay ? 700 : 600}
                      >
                        {p.accumulated_total}
                      </text>
                    )}
                    {/* X-axis label */}
                    <text
                      x={getX(i)}
                      y={chartH - padding.bottom + 18}
                      textAnchor="middle"
                      className="fill-gray-500 dark:fill-gray-400"
                      fontSize={9}
                      transform={totalPoints > 8 ? `rotate(-30, ${getX(i)}, ${chartH - padding.bottom + 18})` : undefined}
                    >
                      {p.label}
                    </text>
                  </g>
                ))}
              </svg>

              {/* Legend */}
              {decayPoints.length > 0 && (
                <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <span>Acumulado por trimestre</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span>Decay (50% ciclagem)</span>
                  </div>
                </div>
              )}
            </div>
          )
        })()}
      </div>

      {/* Monthly Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          Evolução Mensal {selectedYear}
        </h3>

        {isLoadingScores ? (
          <div className="h-48 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <>
            {/* Bars */}
            <div className="flex items-end gap-1 h-48 mb-2">
              {monthlyPoints.map((m, i) => {
                const height = maxChartValue > 0 ? (Math.abs(m.points) / maxChartValue) * 100 : 0
                const isPositive = m.points >= 0
                const quarterBorder = i > 0 && i % 3 === 0

                return (
                  <div key={m.month} className={`flex-1 flex flex-col items-center justify-end h-full ${quarterBorder ? 'border-l-2 border-gray-200 dark:border-gray-600 pl-1' : ''}`}>
                    {m.points !== 0 && (
                      <span className={`text-xs font-semibold mb-1 ${isPositive ? 'text-success' : 'text-danger'}`}>
                        {m.points > 0 ? '+' : ''}{m.points}
                      </span>
                    )}
                    <div
                      className={`w-full max-w-[40px] rounded-t transition-all ${
                        m.points === 0
                          ? 'bg-gray-200 dark:bg-gray-700'
                          : isPositive
                          ? 'bg-success'
                          : 'bg-danger'
                      }`}
                      style={{ height: `${m.points === 0 ? 2 : height}%`, minHeight: '2px' }}
                      title={`${MONTH_FULL_NAMES[i]}: ${m.points > 0 ? '+' : ''}${m.points} pts`}
                    />
                  </div>
                )
              })}
            </div>

            {/* Month Labels */}
            <div className="flex gap-1">
              {monthlyPoints.map((m, i) => (
                <div key={m.month} className="flex-1 text-center">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {MONTH_NAMES[i]}
                  </span>
                </div>
              ))}
            </div>

            {/* Quarter Averages */}
            <div className="flex gap-1 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              {quarterAverages.map((q) => (
                <div key={q.quarter} className="flex-1 text-center">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Q{q.quarter}: </span>
                  <span className={`text-xs font-semibold ${q.average > 0 ? 'text-success' : q.average < 0 ? 'text-danger' : 'text-gray-500'}`}>
                    {q.average > 0 ? '+' : ''}{q.average}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Assessment History */}
      <div className="mb-6">
        {/* History Header with Quarter Filter */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Avaliações
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
              {activeScores.length} {activeScores.length === 1 ? 'registro' : 'registros'}
            </span>
          </h3>

          {/* Quarter Filter Tabs */}
          <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-btn p-1">
            <button
              onClick={() => handleQuarterChange(null)}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                selectedQuarter === null
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Todos
            </button>
            {[1, 2, 3, 4].map((q) => (
              <button
                key={q}
                onClick={() => handleQuarterChange(q)}
                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                  selectedQuarter === q
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Q{q}
              </button>
            ))}
          </div>
        </div>

        {isLoadingScores ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
          </div>
        ) : activeScores.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-card shadow p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              Nenhuma avaliação {selectedQuarter ? `no Q${selectedQuarter} de` : 'em'} {selectedYear}.
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {paginatedScores.map((score) => (
                <div key={score.id} className="bg-white dark:bg-gray-800 rounded-card shadow overflow-hidden">
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">
                          {MONTH_FULL_NAMES[score.month - 1]}/{score.year}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {getQuarterLabel(score.month)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xl font-bold ${score.points_snapshot > 0 ? 'text-success' : score.points_snapshot < 0 ? 'text-danger' : 'text-gray-500'}`}>
                          {score.points_snapshot > 0 ? '+' : ''}{score.points_snapshot} pts
                        </span>
                        {canEdit && (
                          <button
                            onClick={() => openEdit(score)}
                            className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-btn text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            Editar
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Attitudes */}
                    {score.selected_attitudes_detail && score.selected_attitudes_detail.length > 0 && (
                      <div className="mb-3">
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Atitudes:</p>
                        <div className="flex flex-wrap gap-2">
                          {score.selected_attitudes_detail.map((att) => (
                            <span
                              key={att.id}
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                att.type === 'positive'
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                              }`}
                            >
                              {att.name} ({att.points > 0 ? '+' : ''}{att.points})
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Observation */}
                    {score.observation && (
                      <div className="bg-gray-50 dark:bg-gray-900/50 rounded-btn p-3">
                        <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                          "{score.observation}"
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="text-center mt-4">
                <button
                  onClick={() => setVisibleCount((prev) => prev + ITEMS_PER_PAGE)}
                  className="px-6 py-2 text-sm font-medium text-primary hover:text-primary-hover border border-primary/30 hover:border-primary rounded-btn transition-colors"
                >
                  Ver mais ({activeScores.length - visibleCount} restantes)
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Modal */}
      {editingScore && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setEditingScore(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="bg-white dark:bg-gray-800 rounded-card shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Editar — {MONTH_FULL_NAMES[editingScore.month - 1]}/{editingScore.year}
                  </h3>
                  <button
                    onClick={() => setEditingScore(null)}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Points Preview */}
                <div className="bg-primary/10 border border-primary/20 rounded-btn p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Pontuação:</span>
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">
                      {calculateEditPoints() > 0 ? '+' : ''}{calculateEditPoints()} pontos
                    </span>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => setActiveEditTab('positive')}
                    className={`flex-1 py-2 rounded-btn text-sm font-medium transition-colors ${
                      activeEditTab === 'positive'
                        ? 'bg-success text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    Positivas
                  </button>
                  <button
                    onClick={() => setActiveEditTab('negative')}
                    className={`flex-1 py-2 rounded-btn text-sm font-medium transition-colors ${
                      activeEditTab === 'negative'
                        ? 'bg-danger text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    Negativas
                  </button>
                </div>

                {/* Attitudes Grid */}
                <div className="grid grid-cols-1 gap-4 mb-6 max-h-[40vh] overflow-y-auto">
                  {(activeEditTab === 'positive' ? positiveAttitudes : negativeAttitudes).map((attitude) => (
                    <AttitudeCard
                      key={attitude.id}
                      attitude={attitude}
                      isSelected={editAttitudes.has(attitude.id)}
                      onToggle={toggleEditAttitude}
                    />
                  ))}
                </div>

                {/* Observation */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Observação
                  </label>
                  <textarea
                    value={editObservation}
                    onChange={(e) => setEditObservation(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    placeholder="Observação..."
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setEditingScore(null)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={saveEdit}
                    disabled={updateMutation.isPending || editAttitudes.size === 0}
                    className="px-6 py-2 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors disabled:opacity-50"
                  >
                    {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Antifoundation Modal */}
      <ConfirmModal
        isOpen={showAntifoundationModal}
        onClose={() => {
          setShowAntifoundationModal(false)
          setAntifoundationId(null)
        }}
        onConfirm={confirmAntifoundation}
        title="Confirmar Antifundador"
        description={
          <div className="space-y-3">
            <p className="font-semibold text-danger">Esta é uma ação crítica!</p>
            <p>Ao selecionar Antifundador, consequências severas serão aplicadas.</p>
          </div>
        }
        confirmText="Sim, Confirmar"
        cancelText="Cancelar"
        isDanger
      />
    </div>
  )
}

export default EmployeeDetailPage
